import { createSlice, PayloadAction, Dispatch } from "@reduxjs/toolkit";
import { GoogleTokenConfig } from "../../../components/GoogleSheets/cfg";
import { getServicesManager } from "../../../services";
import { RequestType } from "../../../components/GoogleSheets/useGetWindowMessages";

// Auth State
export interface GoogleSheetsAuthState {
  tokenConfig: GoogleTokenConfig | null;
  isSignedIn: boolean;
  isVerifying: boolean;
  authError: string | null;
  statusMessage: string | null;
}

// Window Messages State
export interface PendingAuthCheck {
  spreadsheetId: string;
  sheetGid?: string;
}

export interface PendingTaskDataRequest {
  spreadsheetId: string;
  sheetGid?: string;
  pagination?: {
    page: number;
    page_size: number;
  };
}

export interface PendingColumnOptionsRequest {
  spreadsheetId: string;
  sheetGid?: string;
}

export interface PendingCallLogUpdate {
  spreadsheetId: string;
  sheetGid?: string;
  data: any;
}

export interface RecentlyGrantedAccess {
  spreadsheetId: string;
  grantedAt: number;
  retryAttempt: number;
  isRetrying: boolean;
  lastError?: string;
}

export interface WindowMessagesState {
  latestSpreadsheetIdRequested: string | null;
  pendingAuthCheck: PendingAuthCheck | null;
  pendingTaskDataRequest: PendingTaskDataRequest | null;
  pendingColumnOptionsRequest: PendingColumnOptionsRequest | null;
  pendingCallLogUpdate: PendingCallLogUpdate | null;
  recentlyGrantedAccess: RecentlyGrantedAccess | null;
}

// Spreadsheet Data State
export interface SpreadsheetData {
  id: string;
  name: string;
  sheets: Array<{
    title: string;
    sheetId: number;
    rowCount: number;
    columnCount: number;
  }>;
  data?: any[][];
}

export interface SpreadsheetDataState {
  spreadsheetAccess: string[]; // Array of spreadsheet IDs we have access to
  allSpreadsheetData: SpreadsheetData[];
  spreadsheetIdToLoading: Record<string, boolean>;
  spreadsheetIdToError: Record<string, string | null>;
  spreadsheetIdToAccessChecked: Record<string, boolean>;
  spreadsheetIdToCheckingAccess: Record<string, boolean>;
  triggerAccessCheckFor: string | null; // Spreadsheet ID to trigger access check for
}

// Combined State
export interface GoogleSheetsState {
  auth: GoogleSheetsAuthState;
  windowMessages: WindowMessagesState;
  spreadsheetData: SpreadsheetDataState;
}

const initialState: GoogleSheetsState = {
  auth: {
    tokenConfig: null,
    isSignedIn: false,
    isVerifying: false,
    authError: null,
    statusMessage: null,
  },
  windowMessages: {
    latestSpreadsheetIdRequested: null,
    pendingAuthCheck: null,
    pendingTaskDataRequest: null,
    pendingColumnOptionsRequest: null,
    pendingCallLogUpdate: null,
    recentlyGrantedAccess: null,
  },
  spreadsheetData: {
    spreadsheetAccess: [],
    allSpreadsheetData: [],
    spreadsheetIdToLoading: {},
    spreadsheetIdToError: {},
    spreadsheetIdToAccessChecked: {},
    spreadsheetIdToCheckingAccess: {},
    triggerAccessCheckFor: null,
  },
};

export const googleSheetsSlice = createSlice({
  name: 'googleSheets',
  initialState,
  reducers: {
    // Auth reducers
    setTokenConfig: (state, action: PayloadAction<GoogleTokenConfig | null>) => {
      state.auth.tokenConfig = action.payload;
      state.auth.isSignedIn = action.payload !== null;
    },
    setIsVerifying: (state, action: PayloadAction<boolean>) => {
      state.auth.isVerifying = action.payload;
    },
    setAuthError: (state, action: PayloadAction<string | null>) => {
      state.auth.authError = action.payload;
    },
    setStatusMessage: (state, action: PayloadAction<string | null>) => {
      state.auth.statusMessage = action.payload;
    },
    clearAuth: (state) => {
      state.auth.tokenConfig = null;
      state.auth.isSignedIn = false;
      state.auth.isVerifying = false;
      state.auth.authError = null;
      state.auth.statusMessage = null;
    },

    // Window Messages reducers
    setLatestSpreadsheetIdRequested: (state, action: PayloadAction<string | null>) => {
      state.windowMessages.latestSpreadsheetIdRequested = action.payload;
    },
    setPendingAuthCheck: (state, action: PayloadAction<PendingAuthCheck | null>) => {
      state.windowMessages.pendingAuthCheck = action.payload;
    },
    setPendingTaskDataRequest: (state, action: PayloadAction<PendingTaskDataRequest | null>) => {
      state.windowMessages.pendingTaskDataRequest = action.payload;
    },
    setPendingColumnOptionsRequest: (state, action: PayloadAction<PendingColumnOptionsRequest | null>) => {
      state.windowMessages.pendingColumnOptionsRequest = action.payload;
    },
    setPendingCallLogUpdate: (state, action: PayloadAction<PendingCallLogUpdate | null>) => {
      state.windowMessages.pendingCallLogUpdate = action.payload;
    },
    clearPendingAuthCheck: (state) => {
      state.windowMessages.pendingAuthCheck = null;
    },
    clearPendingTaskDataRequest: (state) => {
      state.windowMessages.pendingTaskDataRequest = null;
    },
    clearPendingColumnOptionsRequest: (state) => {
      state.windowMessages.pendingColumnOptionsRequest = null;
    },
    clearPendingCallLogUpdate: (state) => {
      state.windowMessages.pendingCallLogUpdate = null;
    },
    setRecentlyGrantedAccess: (state, action: PayloadAction<RecentlyGrantedAccess | null>) => {
      state.windowMessages.recentlyGrantedAccess = action.payload;
    },
    updateRecentlyGrantedAccessRetry: (state, action: PayloadAction<{ retryAttempt: number; isRetrying: boolean; lastError?: string }>) => {
      if (state.windowMessages.recentlyGrantedAccess) {
        state.windowMessages.recentlyGrantedAccess.retryAttempt = action.payload.retryAttempt;
        state.windowMessages.recentlyGrantedAccess.isRetrying = action.payload.isRetrying;
        if (action.payload.lastError !== undefined) {
          state.windowMessages.recentlyGrantedAccess.lastError = action.payload.lastError;
        }
      }
    },
    clearRecentlyGrantedAccess: (state) => {
      state.windowMessages.recentlyGrantedAccess = null;
    },

    // Spreadsheet Data reducers
    setSpreadsheetAccess: (state, action: PayloadAction<string[]>) => {
      state.spreadsheetData.spreadsheetAccess = action.payload;
    },
    addSpreadsheetAccess: (state, action: PayloadAction<string>) => {
      if (!state.spreadsheetData.spreadsheetAccess.includes(action.payload)) {
        state.spreadsheetData.spreadsheetAccess.push(action.payload);
      }
    },
    removeSpreadsheetAccess: (state, action: PayloadAction<string>) => {
      state.spreadsheetData.spreadsheetAccess = state.spreadsheetData.spreadsheetAccess.filter(
        id => id !== action.payload
      );
    },
    setAllSpreadsheetData: (state, action: PayloadAction<SpreadsheetData[]>) => {
      state.spreadsheetData.allSpreadsheetData = action.payload;
    },
    addSpreadsheetData: (state, action: PayloadAction<SpreadsheetData>) => {
      const existingIndex = state.spreadsheetData.allSpreadsheetData.findIndex(
        data => data.id === action.payload.id
      );
      if (existingIndex >= 0) {
        state.spreadsheetData.allSpreadsheetData[existingIndex] = action.payload;
      } else {
        state.spreadsheetData.allSpreadsheetData.push(action.payload);
      }
    },
    setSpreadsheetLoading: (state, action: PayloadAction<{ id: string; loading: boolean }>) => {
      state.spreadsheetData.spreadsheetIdToLoading[action.payload.id] = action.payload.loading;
    },
    setSpreadsheetError: (state, action: PayloadAction<{ id: string; error: string | null }>) => {
      state.spreadsheetData.spreadsheetIdToError[action.payload.id] = action.payload.error;
    },
    setSpreadsheetAccessChecked: (state, action: PayloadAction<{ id: string; checked: boolean }>) => {
      state.spreadsheetData.spreadsheetIdToAccessChecked[action.payload.id] = action.payload.checked;
    },
    setSpreadsheetCheckingAccess: (state, action: PayloadAction<{ id: string; checking: boolean }>) => {
      state.spreadsheetData.spreadsheetIdToCheckingAccess[action.payload.id] = action.payload.checking;
    },
    triggerAccessCheckFor: (state, action: PayloadAction<string>) => {
      state.spreadsheetData.triggerAccessCheckFor = action.payload;
    },
    clearAccessCheckTrigger: (state) => {
      state.spreadsheetData.triggerAccessCheckFor = null;
    },
  },
});

export const {
  // Auth actions
  setTokenConfig,
  setIsVerifying,
  setAuthError,
  setStatusMessage,
  clearAuth,
  
  // Window Messages actions
  setLatestSpreadsheetIdRequested,
  setPendingAuthCheck,
  setPendingTaskDataRequest,
  setPendingColumnOptionsRequest,
  setPendingCallLogUpdate,
  clearPendingAuthCheck,
  clearPendingTaskDataRequest,
  clearPendingColumnOptionsRequest,
  clearPendingCallLogUpdate,
  setRecentlyGrantedAccess,
  updateRecentlyGrantedAccessRetry,
  clearRecentlyGrantedAccess,
  
  // Spreadsheet Data actions
  setSpreadsheetAccess,
  addSpreadsheetAccess,
  removeSpreadsheetAccess,
  setAllSpreadsheetData,
  addSpreadsheetData,
  setSpreadsheetLoading,
  setSpreadsheetError,
  setSpreadsheetAccessChecked,
  setSpreadsheetCheckingAccess,
  triggerAccessCheckFor,
  clearAccessCheckTrigger,
} = googleSheetsSlice.actions;

// Async thunks for auth operations
export function loadTokenConfig(dispatch: Dispatch, force_refresh?: boolean): Promise<GoogleTokenConfig | undefined> {
  return new Promise(async (resolve) => {
    try {
      dispatch(setIsVerifying(true));
      const currentToken = await getServicesManager().getCurrentToken(force_refresh);
      if (currentToken) {
        dispatch(setTokenConfig(currentToken));
        resolve(currentToken);
      } else {
        dispatch(setTokenConfig(null));
        resolve(undefined);
      }
    } catch (error) {
      dispatch(setAuthError(error instanceof Error ? error.message : 'Failed to load token'));
      resolve(undefined);
    } finally {
      dispatch(setIsVerifying(false));
    }
  });
}

export function exchangeCodeForTokens(dispatch: Dispatch, code: string, redirectUri: string): Promise<boolean> {
  return new Promise(async (resolve) => {
    try {
      dispatch(setAuthError(null));
      dispatch(setStatusMessage('Authenticating...'));
      
      const tokenConfig = await getServicesManager().exchangeCodeForTokens(code, redirectUri);
      if (!tokenConfig) {
        dispatch(setAuthError('Failed to exchange code for tokens'));
        resolve(false);
      } else {
        dispatch(setTokenConfig(tokenConfig));
        dispatch(setStatusMessage(null));
        resolve(true);
      }
    } catch (error) {
      dispatch(setAuthError(error instanceof Error ? error.message : 'Token exchange failed'));
      dispatch(setStatusMessage(null));
      resolve(false);
    }
  });
}

export function signOut(dispatch: Dispatch) {
  dispatch(clearAuth());
  // Clear any related spreadsheet access when signing out
  dispatch(setSpreadsheetAccess([]));
  dispatch(setAllSpreadsheetData([]));
}

// Selectors
export const selectGoogleSheetsAuth = (state: { googleSheets: GoogleSheetsState }) => state.googleSheets.auth;
export const selectGoogleSheetsWindowMessages = (state: { googleSheets: GoogleSheetsState }) => state.googleSheets.windowMessages;
export const selectGoogleSheetsSpreadsheetData = (state: { googleSheets: GoogleSheetsState }) => state.googleSheets.spreadsheetData;

// Computed selectors
export const selectHasSpreadsheetAccess = (state: { googleSheets: GoogleSheetsState }, spreadsheetId: string | null) => {
  if (!spreadsheetId) return false;
  return state.googleSheets.spreadsheetData.spreadsheetAccess.includes(spreadsheetId);
};

export const selectIsCheckingSpreadsheetAccess = (state: { googleSheets: GoogleSheetsState }, spreadsheetId: string | null) => {
  if (!spreadsheetId) return false;
  return state.googleSheets.spreadsheetData.spreadsheetIdToCheckingAccess[spreadsheetId] || false;
};

export const selectSpreadsheetError = (state: { googleSheets: GoogleSheetsState }, spreadsheetId: string | null) => {
  if (!spreadsheetId) return null;
  return state.googleSheets.spreadsheetData.spreadsheetIdToError[spreadsheetId] || null;
};

export const selectIsSpreadsheetLoading = (state: { googleSheets: GoogleSheetsState }, spreadsheetId: string | null) => {
  if (!spreadsheetId) return false;
  return state.googleSheets.spreadsheetData.spreadsheetIdToLoading[spreadsheetId] || false;
};
